import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import type { FarmStatus } from '@/lib/farm/status';

export type ProducerMemberRole = 'admin' | 'editor' | 'viewer';
export type ProducerBusinessStatus = 'pending' | 'active' | 'paused' | 'banned';

export interface MyProducer {
  id: string;
  name: string;
  slug: string | null;
  status: FarmStatus;
  producerStatus: ProducerBusinessStatus;
  role: ProducerMemberRole;
  place_id: string | null;
  short_description: string | null;
  description: string | null;
  city: string | null;
  county: string | null;
  region: string | null;
  address_text: string | null;
  lat: number | null;
  lng: number | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  instagram: string | null;
  facebook: string | null;
  opening_hours_text: string | null;
  farm_shop_intro: string | null;
  farm_shop_opening_hours_text: string | null;
  farm_shop_status: string;
  categories: string[];
  created_at: string;
  updated_at: string;
  verification_submitted_at: string | null;
  legal_type: string | null;
  org_number: string | null;
  verified: boolean;
  verified_at: string | null;
  address_override_enabled: boolean;
  address_override_text: string | null;
  municipality_override: string | null;
  region_override: string | null;
  lat_override: number | null;
  lng_override: number | null;
  shop_enabled: boolean;
  phone_public: boolean;
  email_public: boolean;
  website_public: boolean;
  instagram_public: boolean;
  facebook_public: boolean;
  has_farm_shop: boolean;
  has_farm_sale_alcohol: boolean;
}

/**
 * Map database status to FarmStatus type
 */
function mapStatus(status: string): FarmStatus {
  if (status === 'public') return 'public';
  // Default to draft for any other status
  return 'draft';
}

interface UseMyProducersOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch producers owned/managed by the current user
 */
export function useMyProducers(options: UseMyProducersOptions = {}) {
  const { user, isLoading: authLoading } = useAuth();
  const queryEnabled = options.enabled ?? true;

  const query = useQuery({
    queryKey: ['my-producers', user?.id],
    queryFn: async (): Promise<MyProducer[]> => {
      // First get membership info
      const { data: memberships, error: memberError } = await supabase
        .from('producer_members')
        .select(`
          role,
          producer_id
        `);

      if (memberError) {
        console.error('Error fetching memberships:', memberError);
        throw memberError;
      }

      if (!memberships || memberships.length === 0) {
        return [];
      }

      // Get producer details for each membership
      const producerIds = memberships.map((m) => m.producer_id);
      const { data: producers, error: producerError } = await supabase
        .from('producers')
        .select(`
          id,
          name,
          slug,
          status,
          producer_status,
          place_id,
          short_description,
          description,
          city,
          county,
          region,
          address_text,
          lat,
          lng,
          phone,
          email,
          website,
          instagram,
          facebook,
          opening_hours_text,
          farm_shop_intro,
          farm_shop_opening_hours_text,
          categories,
          created_at,
          updated_at,
          verification_submitted_at,
          legal_type,
          org_number,
          verified,
          verified_at,
          address_override_enabled,
          address_override_text,
          municipality_override,
          region_override,
          lat_override,
          lng_override,
          shop_enabled,
          phone_public,
          email_public,
          website_public,
          instagram_public,
          facebook_public,
          has_farm_shop,
          has_farm_sale_alcohol,
          farm_shop_status
        `)
        .in('id', producerIds)
        .order('updated_at', { ascending: false });

      if (producerError) {
        console.error('Error fetching producers:', producerError);
        throw producerError;
      }

      // Merge producer data with role
      const roleMap = new Map(memberships.map((m) => [m.producer_id, m.role]));
      
      return (producers || []).map((p) => ({
        ...p,
        status: mapStatus(p.status),
        producerStatus: (p.producer_status || 'pending') as ProducerBusinessStatus,
        role: (roleMap.get(p.id) || 'viewer') as ProducerMemberRole,
        place_id: p.place_id || null,
        verification_submitted_at: p.verification_submitted_at || null,
        legal_type: p.legal_type || null,
        org_number: p.org_number || null,
        verified: p.verified ?? false,
        verified_at: p.verified_at || null,
        address_override_enabled: p.address_override_enabled ?? false,
        address_override_text: p.address_override_text || null,
        municipality_override: p.municipality_override || null,
        region_override: p.region_override || null,
        lat_override: p.lat_override ?? null,
        lng_override: p.lng_override ?? null,
        shop_enabled: (p as any).shop_enabled ?? false,
        phone_public: p.phone_public ?? false,
        email_public: p.email_public ?? false,
        website_public: p.website_public ?? false,
        instagram_public: p.instagram_public ?? false,
        facebook_public: p.facebook_public ?? false,
        facebook: p.facebook || null,
        has_farm_shop: p.has_farm_shop ?? false,
        has_farm_sale_alcohol: p.has_farm_sale_alcohol ?? false,
        farm_shop_status: (p as any).farm_shop_status || 'closed',
      }));
    },
    enabled: queryEnabled && !!user?.id && !authLoading,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    producers: query.data || [],
    hasProducers: (query.data?.length || 0) > 0,
    isLoading: query.isLoading || authLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to fetch a single producer by ID
 */
export function useProducerById(id: string | undefined) {
  const query = useQuery({
    queryKey: ['producer', id],
    queryFn: async (): Promise<MyProducer | null> => {
      if (!id || id === 'demo') {
        return null;
      }

      // Get membership for role
      const { data: membership } = await supabase
        .from('producer_members')
        .select('role')
        .eq('producer_id', id)
        .maybeSingle();

      // Get producer details
      const { data, error } = await supabase
        .from('producers')
        .select(`
          id,
          name,
          slug,
          status,
          producer_status,
          place_id,
          short_description,
          description,
          city,
          county,
          region,
          address_text,
          lat,
          lng,
          phone,
          email,
          website,
          instagram,
          facebook,
          opening_hours_text,
          farm_shop_intro,
          farm_shop_opening_hours_text,
          categories,
          created_at,
          updated_at,
          verification_submitted_at,
          legal_type,
          org_number,
          verified,
          verified_at,
          address_override_enabled,
          address_override_text,
          municipality_override,
          region_override,
          lat_override,
          lng_override,
          shop_enabled,
          phone_public,
          email_public,
          website_public,
          instagram_public,
          facebook_public,
          has_farm_shop,
          has_farm_sale_alcohol,
          farm_shop_status,
          offering_note,
          food_description,
          food_serving_types
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching producer:', error);
        throw error;
      }

      if (!data) {
        return null;
      }

      return {
        ...data,
        status: mapStatus(data.status),
        producerStatus: (data.producer_status || 'pending') as ProducerBusinessStatus,
        role: (membership?.role || 'viewer') as ProducerMemberRole,
        place_id: data.place_id || null,
        verification_submitted_at: data.verification_submitted_at || null,
        legal_type: data.legal_type || null,
        org_number: data.org_number || null,
        verified: data.verified ?? false,
        verified_at: data.verified_at || null,
        address_override_enabled: data.address_override_enabled ?? false,
        address_override_text: data.address_override_text || null,
        municipality_override: data.municipality_override || null,
        region_override: data.region_override || null,
        lat_override: data.lat_override ?? null,
        lng_override: data.lng_override ?? null,
        shop_enabled: (data as any).shop_enabled ?? false,
        phone_public: data.phone_public ?? false,
        email_public: data.email_public ?? false,
        website_public: data.website_public ?? false,
        instagram_public: data.instagram_public ?? false,
        facebook_public: data.facebook_public ?? false,
        facebook: data.facebook || null,
        has_farm_shop: data.has_farm_shop ?? false,
        has_farm_sale_alcohol: data.has_farm_sale_alcohol ?? false,
        farm_shop_status: (data as any).farm_shop_status || 'closed',
      };
    },
    enabled: !!id && id !== 'demo',
  });

  return {
    producer: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to create a new producer via RPC
 */
export function useCreateProducer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      name: string;
      city?: string;
      county?: string;
      shortDescription?: string;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('create_producer_for_user', {
        p_name: params.name,
        p_city: params.city || null,
        p_county: params.county || null,
        p_short_description: params.shortDescription || null,
      });

      if (error) {
        console.error('Error creating producer:', error);
        throw error;
      }

      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-producers'] });
    },
  });
}

/**
 * Hook to update a producer
 */
export function useUpdateProducer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      updates: Partial<{
        name: string;
        slug: string;
        short_description: string;
        description: string;
        city: string;
        county: string;
        region: string;
        address_text: string;
        lat: number | null;
        lng: number | null;
        phone: string;
        email: string;
        website: string;
        instagram: string;
        opening_hours_text: string;
        farm_shop_intro: string;
        farm_shop_opening_hours_text: string;
        categories: string[];
        status: string;
        legal_type: string | null;
        org_number: string | null;
        address_override_enabled: boolean;
        address_override_text: string | null;
        municipality_override: string | null;
        region_override: string | null;
        lat_override: number | null;
        lng_override: number | null;
        shop_enabled: boolean;
        phone_public: boolean;
        email_public: boolean;
        website_public: boolean;
        instagram_public: boolean;
        has_farm_shop: boolean;
        has_farm_sale_alcohol: boolean;
        farm_shop_status: string;
      }>;
    }): Promise<void> => {
      const { error } = await supabase
        .from('producers')
        .update(params.updates)
        .eq('id', params.id);

      if (error) {
        console.error('Error updating producer:', error);
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['producer', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['my-producers'] });
      queryClient.invalidateQueries({ queryKey: ['producer-capabilities', variables.id] });
    },
  });
}
